// PATH: src/pages/admin/tabs/LinksTab.jsx
// VERSION: 1.1 — Admin Enlaces con categorías, theme brand y hovers

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Heading,
  Spinner,
  Select,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiRefreshCw,
  FiSave,
  FiPlusCircle,
  FiEdit2,
  FiTrash2,
} from 'react-icons/fi';
import axiosInstance from '../../../api/axiosConfig';



export default function LinksTab() {
  const toast = useToast();

  const headingColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const theadBg = useColorModeValue('brand.100', 'whiteAlpha.200');
  const rowHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');

  const bw = {
    box: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.200', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'xl',
      boxShadow: 'sm',
    },
    input: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _placeholder: { color: useColorModeValue('brand.400', 'text.muted'), fontWeight: 'semibold' },
      _hover: { borderColor: useColorModeValue('brand.400', 'brand.300') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
    btn: {
      borderRadius: 'md',
      bg: 'accent.500',
      border: '1px solid',
      borderColor: 'accent.500',
      color: 'white',
      fontWeight: 'semibold',
      _hover: {
        bg: 'accent.600',
        borderColor: 'accent.600',
        transform: 'translateY(-1px)',
        boxShadow: 'md',
      },
      _active: {
        transform: 'translateY(0)',
        boxShadow: 'sm',
      },
    },
    btnGhost: {
      borderRadius: 'md',
      bg: useColorModeValue('white', 'whiteAlpha.100'),
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      color: useColorModeValue('brand.700', 'text.default'),
      fontWeight: 'semibold',
      _hover: {
        bg: useColorModeValue('brand.50', 'whiteAlpha.200'),
        borderColor: useColorModeValue('brand.400', 'brand.300'),
      },
    },
    select: {
      border: '1px solid',
      borderColor: useColorModeValue('brand.300', 'border.default'),
      bg: useColorModeValue('white', 'bg.card'),
      color: useColorModeValue('gray.800', 'text.default'),
      borderRadius: 'md',
      _hover: { borderColor: useColorModeValue('brand.400', 'brand.300') },
      _focus: {
        borderColor: 'accent.500',
        boxShadow: '0 0 0 1px var(--chakra-colors-accent-500)',
      },
    },
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [form, setForm] = useState({
    id: null,
    category: '',
    title: '',
    url: '',
    description: '',
  });

  const categories = useMemo(() => {
    const set = new Set(items.map((i) => i.category).filter(Boolean));
    if (form.category) set.add(form.category);
    return Array.from(set).sort();
  }, [items, form.category]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/links/manage', {
        params: { q, category: categoryFilter, limit: 500, offset: 0 },
      });
      setItems(data?.items || []);
    } catch {
      toast({ status: 'error', title: 'Error cargando enlaces' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [q, categoryFilter]);

  function editRow(r) {
    setForm({
      id: r.id,
      category: r.category || '',
      title: r.title || '',
      url: r.url || '',
      description: r.description || '',
    });
  }

  function resetForm() {
    setForm({ id: null, category: '', title: '', url: '', description: '' });
  }

  async function save() {
    const payload = {
      category: form.category.trim(),
      title: form.title.trim(),
      url: form.url.trim(),
      description: form.description || null,
    };
    if (!payload.category || !payload.title || !payload.url) {
      toast({
        status: 'warning',
        title: 'Categoría, título y URL son requeridos',
      });
      return;
    }
    try {
      if (form.id) {
        await axiosInstance.patch(`/links/${form.id}`, payload);
        toast({ status: 'success', title: 'Enlace actualizado' });
      } else {
        await axiosInstance.post('/links', payload);
        toast({ status: 'success', title: 'Enlace creado' });
      }
      resetForm();
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo guardar' });
    }
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar enlace?')) return;
    try {
      await axiosInstance.delete(`/links/${id}`);
      toast({ status: 'success', title: 'Eliminado' });
      load();
    } catch {
      toast({ status: 'error', title: 'No se pudo eliminar' });
    }
  }

  return (
    <Box {...bw.box} p={4}>
      <Heading fontSize="lg" mb={1} color={headingColor}>
        Enlaces
      </Heading>
      <Box fontSize="sm" mb={4} color={subTextColor}>
        Gestiona enlaces importantes (recursos, transmisiones, formularios, etc.).
      </Box>

      <Flex gap={2} mb={3} wrap="wrap">
        <Input
          {...bw.input}
          placeholder="Buscar (título/url/desc)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select
          {...bw.select}
          placeholder="Todas las categorías"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          maxW="240px"
        >
          {categories.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
        <Button
          {...bw.btnGhost}
          leftIcon={<FiRefreshCw />}
          onClick={load}
        >
          Refrescar
        </Button>
      </Flex>

      <Flex gap={2} mb={3} wrap="wrap">
        <Input
          {...bw.input}
          placeholder="Categoría"
          value={form.category}
          onChange={(e) =>
            setForm((f) => ({ ...f, category: e.target.value }))
          }
        />
        <Input
          {...bw.input}
          placeholder="Título"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="URL"
          value={form.url}
          onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
        />
        <Input
          {...bw.input}
          placeholder="Descripción (opcional)"
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
        />
        <Button
          {...bw.btn}
          leftIcon={form.id ? <FiSave /> : <FiPlusCircle />}
          onClick={save}
        >
          {form.id ? 'Actualizar' : 'Crear'}
        </Button>
        {form.id && (
          <Button {...bw.btnGhost} onClick={resetForm}>
            Cancelar
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" py={8}>
          <Spinner thickness="3px" speed="0.65s" />
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table
            size="sm"
            variant="simple"
            sx={{ 'th, td, tr, thead, tbody': { borderColor: 'brand.200' } }}
            border="1px solid"
            borderColor="brand.200"
          >
            <Thead bg={theadBg}>
              <Tr>
                <Th borderColor="brand.200">ID</Th>
                <Th borderColor="brand.200">Categoría</Th>
                <Th borderColor="brand.200">Título</Th>
                <Th borderColor="brand.200">URL</Th>
                <Th borderColor="brand.200">Descripción</Th>
                <Th borderColor="brand.200">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {items.map((r) => (
                <Tr key={r.id} _hover={{ bg: rowHoverBg }}>
                  <Td borderColor="brand.200">{r.id}</Td>
                  <Td borderColor="brand.200">{r.category}</Td>
                  <Td borderColor="brand.200">{r.title}</Td>
                  <Td borderColor="brand.200" maxW="260px" isTruncated>
                    {r.url}
                  </Td>
                  <Td borderColor="brand.200">{r.description || ''}</Td>
                  <Td borderColor="brand.200" whiteSpace="nowrap">
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      mr={2}
                      leftIcon={<FiEdit2 />}
                      onClick={() => editRow(r)}
                    >
                      Editar
                    </Button>
                    <Button
                      {...bw.btnGhost}
                      size="xs"
                      leftIcon={<FiTrash2 />}
                      onClick={() => remove(r.id)}
                    >
                      Eliminar
                    </Button>
                  </Td>
                </Tr>
              ))}
              {items.length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={4} borderColor="brand.200">
                    No hay enlaces registrados.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}
